/**
 * BrandLogo — render a manufacturer's logo via Clearbit Logo API (high-quality
 * full-color brand marks) with two graceful fallbacks: Google Favicon (s2)
 * API and finally an initial-letter yellow tile when both fetch attempts fail.
 *
 * Usage:
 *   <BrandLogo brand="BMW" size={32} />            // 32×32 inline icon
 *   <BrandLogo brand="Vespa" size={64} rounded />  // big, rounded
 */
import { useState } from "react";
import { brandLogoUrl, brandLogoFallbackUrl } from "../../lib/brandLogos";

export default function BrandLogo({
    brand,
    size = 32,
    rounded = false,
    className = "",
    decorative = false,
    "data-testid": testId,
}) {
    // 0 = Clearbit, 1 = Google Favicon, 2 = initial-letter fallback
    const [stage, setStage] = useState(0);
    const requestedSize = size <= 32 ? 64 : Math.min(256, size * 2);
    let url = null;
    if (brand && stage === 0) url = brandLogoUrl(brand, requestedSize);
    else if (brand && stage === 1) url = brandLogoFallbackUrl(brand, requestedSize);
    const useImage = !!url && stage < 2;
    const initial = (brand || "?").trim().charAt(0).toUpperCase();
    const dim = { width: size, height: size };
    const radius = rounded ? "rounded-full" : "rounded-sm";

    if (useImage) {
        return (
            <img
                src={url}
                alt={decorative ? "" : (brand || "")}
                aria-hidden={decorative ? "true" : undefined}
                title={brand || ""}
                onError={() => setStage((s) => s + 1)}
                style={dim}
                className={`${radius} object-contain bg-white border border-gray-200 shrink-0 ${className}`}
                data-testid={testId}
            />
        );
    }
    // Final fallback: yellow initial tile — keeps the layout stable when no
    // logo service is reachable (Wifi offline, hosts blocked, etc.).
    return (
        <span
            style={{ ...dim, fontSize: Math.round(size * 0.5) }}
            className={`${radius} bg-[#FFCC00] text-[#111827] font-display font-bold inline-flex items-center justify-center shrink-0 border border-gray-200 ${className}`}
            title={brand || ""}
            aria-hidden={decorative ? "true" : undefined}
            data-testid={testId}
        >
            {initial}
        </span>
    );
}
